import { Grid, Stack } from '@mui/material';
import { GridRowId, GridSelectionModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import { ContactManagerLayout } from '@modules/ContactManager/components/Tabs/shared/ContactManagerLayout';
import { WorkflowStepNames } from '@modules/ContactManager/components/constants';
import { getOptions } from '@shared/commonFunctions';
import {
  refreshWorkflow,
  selectChangeWorkflowNextStepTo,
  selectRefreshFiles,
  setChangeWorkflowNextStepTo,
  setRefreshWorkflow,
  setWorkflowNextStep,
} from '@store/reducers/contactManagerSlice';
import { AssetsFilter } from '@styles/ContactManager/TabsStyles';
import { DealsButton } from '@styles/StyledButtons';
import { muiTheme } from '@styles/muiTheme';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { SERVICES } from '@shared/constants/apiConfig';
import { useQueryState } from '@shared/hooks/useQueryState';
import { IContactHeaderData, INextStep } from '@modules/ContactManager/components/Header/interfaces';

import { STIP_TYPE_ADD123_CARFAX, STIP_TYPE_REGISTRATION } from '@shared/constants/constants';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { IAsset, IAssetOption } from '../Assets/interfaces';
import { LENDER_CODES } from '../Deals/components/constants';
import { CustomPdfModal } from './components/CustomPdfModal';
import { UploadFilesModal } from './components/UploadFilesModal';
import { FilesTable } from './components/FilesTable';
import { IContactFile, IContactFileResponse, IContactFiles, IFilesData, IGenerateCustomPdfRequest } from './interfaces';
import { requestDownloadAllDealFiles, requestGenerateCustomPdf, requestGetFiles, requestUploadStipFiles } from './requests';

const filesColor = muiTheme.palette.success.main;

export const ContactFiles = ({ deal, reloadDeal, hideAssetDropDown = false, hideDownladAllBtn = false, dealIdParam, assetIdParam, dealSummary }: IContactFiles) => {
  const [contactIdParam] = useQueryState('id');
  const dispatch = useDispatch();
  const refresFiles = useSelector(selectRefreshFiles);
  const updateWorkflowNextStep = useSelector(selectChangeWorkflowNextStepTo);
  const refreshWorkflowSection = useSelector(refreshWorkflow);
  const [assetId, setAssetId] = useState<string>(assetIdParam ?? '');
  const [dealId, setDealId] = useState<string>(dealIdParam ?? '');
  const [assetOptions, setAssetOptions] = useState<IAssetOption[]>([]);
  const [isModalOpen, setModalOpen] = useState(0);
  const [isCustomPdfModalOpen, setCustomPdfModalOpen] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<GridSelectionModel>([]);
  const [fileName, setFileName] = useState('');
  const [filesResponse, setFilesResponse] = useState<IContactFileResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloadDealAfterWorkflowUpdate, setReloadDealAfterWorkflowUpdate] = useState(false);
  const [reloadDealAfterUploadApproval, setReloadDealAfterUploadApproval] = useState(false);
  const [contactFiles, setContactFiles] = useState<IContactFile[]>([]);
  const [contactAssets, setContactAssets] = useState<IAsset[]>();

  const { data: assets } = useSWR<IAsset[]>(`${SERVICES.ASSET}/contacts/${contactIdParam}/assets`);
  const { data: contactDetailsData } = useSWR<IContactHeaderData>(
    contactIdParam && `/${SERVICES.CONTACT}/contacts/${contactIdParam}/header`
  );

  const refreshFilesTable = useCallback(async () => {
    setIsLoading(true);
    const data = await requestGetFiles(contactIdParam, dealId, assetId);
    setFilesResponse(data);
    setIsLoading(false);
  }, [contactIdParam, dealId, assetId]);

  const assetValue = useMemo(() => assetOptions.length > 1 ? assetId : assetOptions[0]?.id ,[assetOptions, assetId]);
  const formatSelectedFiles = useMemo(
    () => {
      const files = filesResponse?.documents.filter(a => !((a.stipType == STIP_TYPE_REGISTRATION || a.stipType == STIP_TYPE_ADD123_CARFAX) && (a.fileName.indexOf('xml') > -1)));
      setContactFiles(files ?? []);
      return filesResponse?.documents?.filter((document: { contactStipId: GridRowId }) =>
        selectedFiles.includes(document.contactStipId)
      ) ?? [];
    },
    [selectedFiles, filesResponse?.documents]
  );

  useEffect(() => {
    setAssetId(assetIdParam ?? '');
    setDealId(dealIdParam ?? '');
  }, [assetIdParam, dealIdParam]);

  useEffect(() => {
    refreshFilesTable();
  }, [assetId, dealId, refresFiles, refreshFilesTable]);

  useEffect(() => {
    if (assets) {
      const options: IAssetOption[] = [];

      assets.forEach((a) => {
        options.push({
          id: a.id,
          display: `${a.year} ${a.make} ${a.model} ${a.vin}`,
        });
      });
      setAssetOptions(options);
      setContactAssets(assets);  
    }
  }, [assets, contactIdParam]);

  useEffect(() => {
    if ((updateWorkflowNextStep == null && reloadDealAfterWorkflowUpdate) || reloadDealAfterUploadApproval) {
      setReloadDealAfterWorkflowUpdate(false);
      setReloadDealAfterUploadApproval(false);
      async () => {
        await reloadDeal?.();
      };
    }
  }, [updateWorkflowNextStep, reloadDealAfterWorkflowUpdate, reloadDealAfterUploadApproval]);

  const handleAssetSelect = (value: string) => {
    setAssetId(value);
    setDealId('');
  };

  const assetDeals = useMemo(() => {
    return dealSummary.filter(r => r.assetId == assetId);
  },[dealSummary, assetId]);


  const handleDownloadAll = useCallback(async () => {
    const dealNumericId = assetDeals.find((deal) => deal.dealId === dealId)?.dealNumericId;
    const contactName = contactDetailsData ? `${contactDetailsData?.lastName ?? ''}, ${contactDetailsData?.firstName ?? ''} ` : '';
    const dealInfo = dealNumericId || dealId ? `Deal ${dealNumericId ?? dealId ?? ''} ` : '';
    const fileName = `${contactName}${dealInfo}files.zip`;
    
    setLoading(true);
    await requestDownloadAllDealFiles(contactFiles.filter(file => !file.isDeleted), fileName);
    setLoading(false);
  }, [dealId, assetId, assetDeals, contactDetailsData, contactFiles]);

  const handleDealSelect = (value: string) => {
    setDealId(value);
  };

  const handleUploadFiles = () => {
    setModalOpen(prevState => prevState + 1);
  };

  const handleSubmit = useCallback(
    async (dataFiles: IFilesData) => {
      dataFiles.docs.forEach(async (doc, index) => {
        const postData = {
          ...doc,
          file: dataFiles.files[index],
          assetId: dataFiles?.assetId || assetId,
          contactId: contactIdParam,
          dealId: dealId,
          borrowerCoBorrowerType: doc.borrowerCoBorrowerType || '',
        };
        setLoading(true);
        const cudlResult = await requestUploadStipFiles(postData);
        await refreshFilesTable();
        setLoading(false);
        // deal re-calculate and workflow update after cudl document upload nextCudlWorkflow
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const nextWorkflowFromCudl = cudlResult?.data;
        if (nextWorkflowFromCudl === WorkflowStepNames.APPROVED || nextWorkflowFromCudl === WorkflowStepNames.COUNTER) {
          dispatch(setChangeWorkflowNextStepTo(nextWorkflowFromCudl));
          setReloadDealAfterWorkflowUpdate(true);
        } else if (nextWorkflowFromCudl === WorkflowStepNames.DECLINED) {
          dispatch(setChangeWorkflowNextStepTo(nextWorkflowFromCudl));
        }
      });
      if (deal?.lenderResponse.lenderCode === LENDER_CODES.QLO || deal?.lenderResponse.isQuilo) {
        setTimeout(() => {dispatch(setRefreshWorkflow(refreshWorkflowSection));
          dispatch(setWorkflowNextStep({} as INextStep));
        }, 3000);
      }

      setModalOpen(0);
    },
    [dealId, assetId]
  );

  const handleCustomPdfClick = useCallback(() => {
    setCustomPdfModalOpen(prevState => prevState + 1);
    setFileName('');
  }, []);

  const handleSelectedFiles = useCallback((files: GridSelectionModel | []) => {
    setSelectedFiles(files);
  }, []);

  const handleFileNameChange = useCallback((fileName: string) => {
    setFileName(fileName);
  }, []);

  const generateCustomPdf = useCallback(async () => {
    const customPdfPostData: IGenerateCustomPdfRequest = {
      contactId: contactIdParam,
      assetId,
      dealId,
      fileName,
      contactStipIds: selectedFiles,
    };
    const generatePdfResponse = await requestGenerateCustomPdf(customPdfPostData);
    if (generatePdfResponse?.status === 200) {
      setCustomPdfModalOpen(0);
      refreshFilesTable();
      setSelectedFiles([]);
    } else {
      setCustomPdfModalOpen(prevState => prevState + 1);
    }
  }, [assetId, dealId, selectedFiles, fileName]);

  return (
    <ContactManagerLayout>
      <LoaderBackdrop open={loading} />
      <AssetsFilter
        color={filesColor}
        justify="end"
      >
        <Stack
          direction="row"
          spacing={2}
          mb={!dealId ? 0 : 1}
          gap={1}
        >
          <DealsButton
            variant="contained"
            type="button"
            disabled={!selectedFiles.length || !assetId || !dealId}
            onClick={handleCustomPdfClick}
          >
            Custom PDF
          </DealsButton>
          {!hideDownladAllBtn && (

            <DealsButton
              disabled={!contactFiles?.length}
              onClick={handleDownloadAll}
            >
              Download All
            </DealsButton>
          )}
          <DealsButton
            variant="contained"
            type="button"
            onClick={handleUploadFiles}
          >
            Upload Files
          </DealsButton>
        </Stack>
      </AssetsFilter>

      {!hideAssetDropDown && (
        <Grid
          container
          alignItems="center"
          spacing={2}
        >
          <Grid
            item
            xs={4}
          >
            <SelectAutocomplete
              removeExtraPadding
              canBeClean={false}
              value={assetValue}
              options={getOptions(assetOptions, 'id', 'display')}
              placeholder="Select Asset"
              onChange={handleAssetSelect}
            />
          </Grid>

          <Grid
            item
            xs={4}
          >
            {assetId && (
              <SelectAutocomplete
                removeExtraPadding
                value={dealId}
                options={getOptions(assetDeals, 'dealId', 'dealNumericId')}
                placeholder="Select Deal"
                onChange={handleDealSelect}
              />
            )}
          </Grid>
        </Grid>
      )}
      <FilesTable
        rows={contactFiles ?? []}
        reloadFiles={refreshFilesTable}
        selectedFiles={selectedFiles}
        handleSelectedFiles={handleSelectedFiles}
        isLoading={isLoading}
        dealId={dealId}
        assetId={assetId}
        assets={contactAssets}
      />
      {!!isModalOpen && (
        <UploadFilesModal
          uploadFilesModalPosition={isModalOpen}
          dealId={dealId}
          assetOptions={assetOptions || []}
          assetId={assetValue || ''}
          handleSubmit={handleSubmit}
          handleClose={() => setModalOpen(0)}
        />
      )}
      {!!isCustomPdfModalOpen && (
        <CustomPdfModal
          pdfModalPosition={isCustomPdfModalOpen}
          selectedFiles={formatSelectedFiles}
          fileName={fileName}
          handleFileNameChange={handleFileNameChange}
          handleClose={() => setCustomPdfModalOpen(0)}
          generateCustomPdf={generateCustomPdf}
        />
      )}
    </ContactManagerLayout>
  );
};
